import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import config from "../config/config";
import { requestBrochureDownload } from "../utils/brochureDownload";

const Page = () => {
  const { navType, subCategory, slug } = useParams();
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarItems, setSidebarItems] = useState([]);
  const [currentPageInfo, setCurrentPageInfo] = useState(null);
  const [pageImages, setPageImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModal, setImageModal] = useState(false);
  const [brochureEmail, setBrochureEmail] = useState("");
  const [brochureError, setBrochureError] = useState("");
  const [brochureSubmitting, setBrochureSubmitting] = useState(false);

  // Update document title when page content changes
  useEffect(() => {
    if (pageContent?.pm_title) {
      document.title = pageContent.pm_title;
    }
  }, [pageContent]);

  // Fetch page images
  const fetchPageImages = async (pageId) => {
    try {
      console.log('Fetching images for page ID:', pageId);
      const response = await fetch(
        config.getApiUrl(`get_page_images.php?page_id=${pageId}`)
      );
      const data = await response.json();
      console.log('Images API response:', data);

      if (data.success) {
        setPageImages(data.data);
        console.log('Set page images:', data.data);
        if (data.gallery_source) {
          console.log('Gallery source info:', data.gallery_source);
        }
      }
    } catch (error) {
      console.error("Error fetching page images:", error);
    }
  };

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        setLoading(true);

        // Call the PHP API with slug-based approach
        const baseApiUrl = config.getApiUrl("get_page_content.php");

        const apiUrl = slug
          ? `${baseApiUrl}?slug=${slug}`
          : subCategory
          ? `${baseApiUrl}?nav=${navType}&sub=${subCategory}`
          : `${baseApiUrl}?nav=${navType}`;

        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.data && data.data.length > 0) {
          setPageContent(data.data[0]);
          // Fetch page images after getting page content
          fetchPageImages(data.data[0].pm_id);
        } else {
          throw new Error(data.message || "No content found");
        }
      } catch (err) {
        console.error("Error fetching page content:", err);
        // Set fallback content
        const title = subCategory
          ? `${subCategory
              .replace(/-/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}`
          : navType
          ? `${navType.charAt(0).toUpperCase() + navType.slice(1)} Section`
          : "Optimising Resources";

        setPageContent({
          pm_title: title,
          pm_content: getDefaultContent(navType, subCategory),
          pm_timestamp: new Date().toISOString(),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPageContent();
  }, [navType, subCategory, slug]);

  // Fetch navigation data to find current page's siblings
  useEffect(() => {
    const fetchNavigationData = async () => {
      try {
        const response = await fetch(config.getApiUrl("get_navigation.php"));
        const data = await response.json();

        if (data.success && data.navigation) {
          const currentSlug = slug || navType;
          console.log("Page.js - Looking for slug:", currentSlug);
          const pageInfo = findCurrentPageInfo(data.navigation, currentSlug);
          console.log("Page.js - Found pageInfo:", pageInfo);

          if (pageInfo) {
            setCurrentPageInfo(pageInfo);
            setSidebarItems(pageInfo.siblings || []);
            console.log("Page.js - Set sidebar items:", pageInfo.siblings?.length || 0, "items");
          } else {
            console.log("Page.js - No pageInfo found for slug:", currentSlug);
          }
        }
      } catch (error) {
        console.error("Error fetching navigation data:", error);
      }
    };

    if (slug || navType) {
      fetchNavigationData();
    }
  }, [slug, navType]);

  // Helper function to find current page info and its navigation items
  const findCurrentPageInfo = (navigationData, targetSlug) => {
    const findInTree = (items, parentItem = null) => {
      for (const item of items) {
        if (item.slug === targetSlug) {
          // Check if this is a main navigation item (has children)
          if (item.children && item.children.length > 0) {
            // For main navigation items, show all their children in sidebar
            return {
              currentPage: item,
              parent: null,
              siblings: item.children,
              isMainNav: true,
            };
          } else if (parentItem && parentItem.children) {
            // For sub-items, show all siblings and main nav children
            return {
              currentPage: item,
              parent: parentItem,
              siblings: parentItem.children,
              isMainNav: false,
            };
          } else {
            // If no parent or children, try to find in main nav
            return {
              currentPage: item,
              parent: null,
              siblings: [],
              isMainNav: false,
            };
          }
        }

        // Search in children
        if (item.children && item.children.length > 0) {
          const result = findInTree(item.children, item);
          if (result) return result;
        }
      }
      return null;
    };

    return findInTree(navigationData);
  };

  const getDefaultContent = (type, subType) => {
    // Handle specific subcategories
    if (subType) {
      const subCategoryContent = {
        "fencing-products": `
          <div class="container">
            <div class="row">
              <div class="col-12">
                <h2>Fencing Products</h2>
                <p>NCG Corporate specializes in manufacturing high-quality fencing products designed for security, durability, and aesthetic appeal. Our comprehensive range includes crowd barriers, chain link gates, expanded mesh gates, anti-climb panels, and various fencing accessories.</p>
                
                <div class="row mt-4">
                  <div class="col-md-6">
                    <h4>Product Range</h4>
                    <ul>
                      <li><strong>Crowd Barriers:</strong> Temporary barriers for crowd control and event management</li>
                      <li><strong>Chain Link Gates:</strong> Durable gates for residential and commercial properties</li>
                      <li><strong>Expanded Mesh Gates:</strong> Security gates with enhanced visibility</li>
                      <li><strong>Anti Climb Panels:</strong> High-security panels to prevent unauthorized access</li>
                      <li><strong>Ground Spikes:</strong> Secure mounting solutions for temporary fencing</li>
                      <li><strong>Fencing Accessories:</strong> Complete range of posts, brackets, and hardware</li>
                    </ul>
                  </div>
                  <div class="col-md-6">
                    <h4>Applications</h4>
                    <p>Our fencing products are ideal for:</p>
                    <ul>
                      <li>Construction sites and industrial facilities</li>
                      <li>Residential and commercial properties</li>
                      <li>Events and temporary installations</li>
                      <li>High-security perimeter protection</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `,
        "animal-handling": `
          <div class="container">
            <div class="row">
              <div class="col-12">
                <h2>Animal Handling Equipment</h2>
                <p>NCG Corporate manufactures robust animal handling equipment designed for farms, livestock facilities, and agricultural operations. Our products ensure safe and efficient handling of various animals while maintaining their welfare.</p>
                
                <div class="row mt-4">
                  <div class="col-md-6">
                    <h4>Product Range</h4>
                    <ul>
                      <li><strong>Farm Gates:</strong> Heavy-duty gates for farm access and livestock control</li>
                      <li><strong>Cattle Hurdles:</strong> Portable panels for cattle management and sorting</li>
                      <li><strong>Sheep Hurdles:</strong> Lightweight panels designed for sheep handling</li>
                      <li><strong>Horse Hurdles:</strong> Safe and secure panels for equine facilities</li>
                    </ul>
                  </div>
                  <div class="col-md-6">
                    <h4>Features</h4>
                    <ul>
                      <li>Galvanized finish for weather resistance</li>
                      <li>Easy assembly and portability</li>
                      <li>Animal-safe design with smooth edges</li>
                      <li>Various sizes to suit different animals</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `,
        "building-hardware": `
          <div class="container">
            <div class="row">
              <div class="col-12">
                <h2>Building Hardware</h2>
                <p>Our comprehensive range of building hardware includes hinges, window accessories, and shelf supports. All products are manufactured to the highest standards and available in various finishes to meet your specific requirements.</p>
                
                <div class="row mt-4">
                  <div class="col-md-6">
                    <h4>Product Categories</h4>
                    <ul>
                      <li><strong>Hinges:</strong> Heavy-duty hinges for doors and gates</li>
                      <li><strong>Window Accessories:</strong> Handles, stays, and security hardware</li>
                      <li><strong>Shelf Supports:</strong> Brackets and supports for various applications</li>
                    </ul>
                  </div>
                  <div class="col-md-6">
                    <h4>Available Finishes</h4>
                    <ul>
                      <li>Hot dip galvanizing</li>
                      <li>Electroplating</li>
                      <li>Powder coating</li>
                      <li>Stainless steel options</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `,
        fastener: `
          <div class="container">
            <div class="row">
              <div class="col-12">
                <h2>Fasteners</h2>
                <p>NCG Corporate sources and supplies a comprehensive range of fasteners including screws, nuts, bolts, and washers. Our fastener solutions cater to various industries and applications with different materials and finishes.</p>
                
                <div class="row mt-4">
                  <div class="col-md-6">
                    <h4>Product Range</h4>
                    <ul>
                      <li><strong>Screws:</strong> Self-tapping, machine screws, wood screws</li>
                      <li><strong>Nuts & Bolts:</strong> Hex bolts, carriage bolts, specialty fasteners</li>
                      <li><strong>Washers:</strong> Flat washers, spring washers, specialty washers</li>
                    </ul>
                  </div>
                  <div class="col-md-6">
                    <h4>Materials & Standards</h4>
                    <ul>
                      <li>Stainless steel (304, 316)</li>
                      <li>Carbon steel with various coatings</li>
                      <li>Brass and aluminum options</li>
                      <li>ISO, DIN, ANSI standards compliance</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `,
      };

      return (
        subCategoryContent[subType] ||
        `
        <div class="container">
          <div class="row">
            <div class="col-12 text-center">
              <div class="alert alert-warning">
                <h4>Page Content Not Found</h4>
                <p>The content for this page is currently not available in our database.</p>
                <p>Please check again soon or contact us for more information.</p>
              </div>
            </div>
          </div>
        </div>
      `
      );
    }

    const defaultContents = {
      manufacturing: `
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h2>Manufacturing Excellence</h2>
              <p>NCG Corporate specializes in manufacturing high-quality steel products and customized engineering solutions. Our state-of-the-art facilities and experienced team ensure precision and quality in every product we deliver.</p>
              <div class="row mt-4">
                <div class="col-md-6">
                  <h4>Our Manufacturing Capabilities</h4>
                  <ul>
                    <li>Fencing Products & Accessories</li>
                    <li>Animal Handling Equipment</li>
                    <li>Angle Brackets & Connectors</li>
                    <li>Scaffolding & Stands</li>
                    <li>Customized Engineering Products</li>
                  </ul>
                </div>
                <div class="col-md-6">
                  <h4>Quality Standards</h4>
                  <p>All our manufacturing processes follow international quality standards and are backed by our 30+ years of industry experience.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      `,
      sourcing: `
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h2>Strategic Sourcing Solutions</h2>
              <p>Leverage our extensive network and expertise in global sourcing to optimize your supply chain and reduce costs while maintaining quality standards.</p>
              <div class="row mt-4">
                <div class="col-md-6">
                  <h4>Sourcing Categories</h4>
                  <ul>
                    <li>Building Hardware</li>
                    <li>Fasteners & Components</li>
                    <li>Hand Tools</li>
                    <li>Aluminum Products</li>
                    <li>Packaging Solutions</li>
                  </ul>
                </div>
                <div class="col-md-6">
                  <h4>Global Network</h4>
                  <p>Our sourcing team has established relationships with trusted suppliers worldwide, ensuring competitive pricing and reliable delivery.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      `,
      services: `
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h2>Comprehensive Services</h2>
              <p>From vendor development to engineering design, we provide end-to-end solutions to streamline your operations and enhance productivity.</p>
              <div class="row mt-4">
                <div class="col-md-6">
                  <h4>Service Offerings</h4>
                  <ul>
                    <li>Vendor Development & Management</li>
                    <li>Engineering Inspection Services</li>
                    <li>Engineering Design Services</li>
                    <li>SCM & Retail Network</li>
                    <li>Procurement Outsourcing</li>
                  </ul>
                </div>
                <div class="col-md-6">
                  <h4>Expert Team</h4>
                  <p>Our team of qualified engineers and Six Sigma certified professionals ensure the highest standards of service delivery.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      `,
    };

    return (
      defaultContents[type] ||
      `
      <div class="container">
        <div class="row">
          <div class="col-12 text-center">
            <div class="alert alert-warning">
              <h4>Page Content Not Found</h4>
              <p>The content for this page is currently not available in our database.</p>
              <p>Please check again soon or contact us for more information.</p>
            </div>
          </div>
        </div>
      </div>
    `
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-12 text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading content...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero Banner Section */}
      <section
        className="optimising-hero-section"
        style={{
          backgroundImage: `url(${config.getImageUrl("ncg_page.png")})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="hero-content-right" style={{ marginTop: "60px" }}>
                <h1 className="optimising-title">
                  {pageContent?.pm_title
                    ? pageContent.pm_title.replace(" - NCG Corporate", "")
                    : "Dynamic Content"}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="page-main-content">
        <div className="container">
          <div className="row">
            {/* Left Content Area */}
            <div className="col-lg-8">
              <div className="main-content-area">
                <h2 className="page-main-title">
                  {pageContent?.pm_title
                    ? pageContent.pm_title.replace(" - NCG Corporate", "")
                    : navType
                    ? `${navType.charAt(0).toUpperCase() + navType.slice(1)}`
                    : "Page Content"}
                </h2>

                <div className="content-text">
                  {pageContent?.pm_content ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: pageContent.pm_content,
                      }}
                    />
                  ) : (
                    <div className="default-content">
                      <div className="alert alert-warning text-center">
                        <h4>Page Content Not Found</h4>
                        <p>
                          The content for this page is currently not available
                          in our database.
                        </p>
                        <p>
                          Please check again soon or contact us for more
                          information.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Page Gallery Section */}
                {console.log('Rendering gallery with pageImages:', pageImages)}
                {pageImages.length > 0 && (
                  <div className="page-gallery-section">
                    <h3 className="gallery-title">Gallery</h3>
                    <div className="gallery-grid">
                      {pageImages.map((image) => (
                        <div
                          key={image.id}
                          className="gallery-item"
                          onClick={() => {
                            setSelectedImage(image);
                            setImageModal(true);
                          }}
                        >
                          <img
                            src={config.getImageUrl(`page_images/${image.image_filename}`)}
                            alt={image.image_title || "Gallery image"}
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                          <div className="gallery-item-overlay">
                            <div className="gallery-item-info">
                              <h5>{image.image_title || "Image"}</h5>
                              {image.image_description && (
                                <p>{image.image_description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="col-lg-4">
              <div className="page-sidebar">
                <div className="sidebar-nav">
                  {/* Dynamic sidebar title based on navigation type */}
                  {currentPageInfo?.isMainNav &&
                    currentPageInfo?.currentPage?.title && (
                      <h5 className="sidebar-title">
                        {currentPageInfo.currentPage.title}
                      </h5>
                    )}
                  {currentPageInfo?.parent && !currentPageInfo?.isMainNav && (
                    <h5 className="sidebar-title">
                      {currentPageInfo.parent.title}
                    </h5>
                  )}
                  {!currentPageInfo?.parent &&
                    !currentPageInfo?.isMainNav &&
                    sidebarItems.length > 0 && (
                      <h5 className="sidebar-title">Related Products</h5>
                    )}

                  {sidebarItems.length > 0 ? (
                    sidebarItems.map((item) => (
                      <Link
                        key={item.id}
                        to={`/page/${item.slug}`}
                        className={`sidebar-nav-link ${
                          item.slug === (slug || navType) ? "active" : ""
                        }`}
                      >
                        {item.icon && <i className={`${item.icon} me-2`}></i>}
                        {item.title}
                        {item.children && item.children.length > 0 && (
                          <i className="fas fa-chevron-right ms-auto"></i>
                        )}
                      </Link>
                    ))
                  ) : (
                    <div className="sidebar-nav-link">
                      No related pages available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download Our Brochure Section */}
      <section
        className="cta-red-section"
        style={{
          backgroundImage: `url(${config.getImageUrl("download_bro.png")})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Transparent red overlay on right half */}
        <div className="cta-overlay"></div>

        {/* Form content positioned over the right side */}
        <div className="cta-content-area">
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBrochureError("");
              setBrochureSubmitting(true);
              const result = await requestBrochureDownload(brochureEmail);
              setBrochureSubmitting(false);
              if (!result.ok) {
                setBrochureError(result.error);
                return;
              }
              setBrochureEmail("");
            }}
            noValidate
          >
            <h3 className="cta-title-new">Download Our Brochure</h3>

            <label htmlFor="brochure-email-page" className="cta-form-label-new">
              Email Address
            </label>
            <input
              type="email"
              id="brochure-email-page"
              name="brochure-email"
              className="cta-email-input-new"
              placeholder="Email Address*"
              value={brochureEmail}
              onChange={(e) => setBrochureEmail(e.target.value)}
              autoComplete="email"
            />
            <button
              type="submit"
              className="cta-know-more-btn-new"
              disabled={brochureSubmitting}
            >
              Know More <i className="fas fa-arrow-right ms-2"></i>
            </button>
            {brochureError ? (
              <p className="cta-brochure-error" role="alert">
                {brochureError}
              </p>
            ) : null}
          </form>
        </div>
      </section>

      {/* Footer Information Section */}
      <section className="page-footer-info-section">
        <div className="container">
          <div className="row">
            {/* Column 1: Spotlight */}
            <div className="col-lg-4 col-md-6">
              <div className="info-column">
                <h6 className="info-title">Spotlight</h6>
                <h5 className="info-heading">Exports of Fencing Accessories</h5>
                <ul className="info-text spotlight-copy-list">
                  <li>
                    Exports of premium fencing accessories, crafted with
                    precision and strength.
                  </li>
                  <li>
                    Engineered for durability, ensuring long-lasting performance
                    in every environment.
                  </li>
                  <li>
                    Manufactured with high-quality materials, meeting global
                    standards and demands.
                  </li>
                  <li>
                    Trusted worldwide for reliability, innovation, and superior
                    finishing.
                  </li>
                </ul>
              </div>
            </div>

            {/* Column 2: Address */}
            <div className="col-lg-4 col-md-6">
              <div className="info-column">
                <h5 className="info-heading">Address</h5>
                <div className="address-info">
                  <p>
                    <strong>Registered Office:</strong>
                  </p>
                  <p>
                    SCO: 117/118(2nd Floor), Sector -17/B,
                    <br />
                    Chandigarh, India, Pin- 160017
                  </p>
                  <p>
                    <strong>Phone:</strong>{" "}
                    <a href="tel:01724014825">0172-4014825</a>
                  </p>
                  <p>
                    <strong>Cell no:</strong>{" "}
                    <a href="tel:+919779044006">+91-9779044006</a>
                  </p>
                  <p>
                    <strong>Email:</strong>{" "}
                    <a href="mailto:info@ncgcorporate.com">
                      info@ncgcorporate.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Column 3: Factory/Corporate Office */}
            <div className="col-lg-4 col-md-6">
              <div className="info-column">
                <h5 className="info-heading">Factory/Corporate Office:</h5>
                <div className="address-info">
                  <p>
                    <strong>Plot No.</strong> C-134, Industrial Area Phase VIII,
                    <br />
                    Sector 72, SAS Nagar Mohali, Punjab,
                    <br />
                    India, Pin- 160071
                  </p>
                  <p>
                    <strong>Cell:</strong>{" "}
                    <a href="tel:+919877078268">+91-9877078268</a> /{" "}
                    <a href="tel:+919779044006">+91-9779044006</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Database Info Section */}
      {pageContent && (
        <section className="page-info-section py-3 bg-light">
          <div className="container">
            <div className="row">
              <div className="col-12 text-center">
                <small className="text-muted">
                  Content ID: {pageContent.pm_id} | Last Updated:{" "}
                  {new Date(pageContent.pm_timestamp).toLocaleDateString()} |
                  Navigation: {navType || "default"}
                </small>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Image Modal */}
      {imageModal && selectedImage && (
        <div className="image-modal">
          <div className="image-modal-content">
            <div className="image-modal-header">
              <h3>{selectedImage.image_title || "Image"}</h3>
              <button
                type="button"
                className="image-modal-close"
                onClick={() => {
                  setImageModal(false);
                  setSelectedImage(null);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="image-modal-body">
              <img
                src={config.getImageUrl(`page_images/${selectedImage.image_filename}`)}
                alt={selectedImage.image_title || "Gallery image"}
                className="image-modal-img"
              />
              {selectedImage.image_description && (
                <div className="image-modal-description">
                  <p>{selectedImage.image_description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        /* Page Gallery Styles */
        .page-gallery-section {
          margin-top: 3rem;
          padding-top: 2rem;
          border-top: 1px solid #e9ecef;
        }

        .gallery-title {
          color: #2c3e50;
          font-weight: 600;
          margin-bottom: 1.5rem;
          font-size: 1.5rem;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
        }

        .gallery-item {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          background: white;
        }

        .gallery-item:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        }

        .gallery-item {
          cursor: pointer;
        }

        .gallery-item img {
          width: 100%;
          height: 200px;
          object-fit: cover;
          display: block;
        }

        .gallery-item-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .gallery-item:hover .gallery-item-overlay {
          opacity: 1;
        }

        .gallery-item-info {
          text-align: center;
          color: white;
          padding: 1rem;
        }

        .gallery-item-info h5 {
          margin: 0 0 0.5rem 0;
          font-weight: 600;
          font-size: 1.1rem;
        }

        .gallery-item-info p {
          margin: 0;
          font-size: 0.9rem;
          line-height: 1.4;
          opacity: 0.9;
        }

        /* Image Modal Styles */
        .image-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 2rem;
        }

        .image-modal-content {
          background: white;
          border-radius: 12px;
          max-width: 90%;
          max-height: 90%;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        .image-modal-header {
          padding: 1.5rem;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .image-modal-header h3 {
          margin: 0;
          color: #2c3e50;
          font-weight: 600;
        }

        .image-modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6c757d;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background 0.2s ease;
        }

        .image-modal-close:hover {
          background: #f8f9fa;
        }

        .image-modal-body {
          padding: 1.5rem;
          text-align: center;
        }

        .image-modal-img {
          max-width: 100%;
          max-height: 60vh;
          object-fit: contain;
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        }

        .image-modal-description {
          margin-top: 1.5rem;
          text-align: left;
        }

        .image-modal-description p {
          margin: 0;
          color: #6c757d;
          line-height: 1.6;
          font-size: 1rem;
        }

        @media (max-width: 768px) {
          .gallery-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }

          .gallery-item img {
            height: 150px;
          }

          .image-modal {
            padding: 1rem;
          }

          .image-modal-content {
            max-width: 95%;
          }

          .image-modal-header,
          .image-modal-body {
            padding: 1rem;
          }
        }

        @media (max-width: 480px) {
          .gallery-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
};

export default Page;
